import {
    AppOrderPaymentTermsEnum,
    BuyerFinancialDocumentTypeEnum,
    SupplierPayoutTermsEnum,
    LifecycleStateEnum,
    DeliveryStateEnum,
    PayInStateEnum,
    PayoutStateEnum,
    LegacyDeliveryStatusEnum,
    LegacyPaymentStatusEnum,
    CatalogItemDocumentCategoryEnum,
    AppOrderDocumentType,
    ServiceTypeEnum,
    ServiceFeeTypeEnum,
    OrderActivityCategory,
    PaymentProviderEnum,
    BuyerOnboardingCountry,
    ShippingResponsibilityEnum,
    ConfirmShippingResponsibilityShippingResponsibilityEnum,
    ReportingUnitsEnum,
    CreditRiskOwnerEnum,
    InternalReasonEnum,
    SupplierTypeEnum,
    QualityIssueTypeEnum,
} from "./types/api/data-contracts";
import type {
    AppOrderPaymentTermsLabelsMap,
    AppOrderSupplierPaymentTermsLabelsMap,
    DeliveryStateLabelsMap,
    LifecycleStateLabelsMap,
    PayoutStateLabelsMap,
    PayInStateLabelsMap,
    SpotBuyOrderDocumentCategory,
    PaymentProviderLabelsMap,
} from "./types/AppOrder";

export const INQUIRY_ICON_FALLBACK =
    "https://storage.googleapis.com/v26kbrqn/inquiry/icons/inquiry_default_icon.png";

export const OPPLY_SQUARE_LOGO_ICON = "/images/logo-square.svg";

/**
 * List of supported allergens on platform for inquiry creation/edit
 */
export const supportedAllergens = [
    "celery",
    "cereals_containing_gluten",
    "crustaceans",
    "eggs",
    "fish",
    "lupin",
    "milk",
    "molluscs",
    "mustard",
    "nuts",
    "peanuts",
    "sesame_seeds",
    "soya",
    "sulphur_dioxide",
];

/**
 * List of supported dietary requirements on platform for inquiry creation/edit
 */
export const supportedDietaryRequirements = [
    "kosher",
    "halal",
    "organic",
    "vegan",
    "vegetarian",
];

/**
 * List of price disclaimer points shown on inquiry creation/edit
 */
export const pricingDisclaimerList = [
    "All prices are given in local currency as per your selected country",
    "Target unit price will not be shown to suppliers",
];

/**
 * List of available frequency options on platform for inquiry creation/edit
 */
export const forecastOptions = [
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Annually", value: "annually" },
];

export const frequencyOptions = forecastOptions.map((option) => option.value);

export const HOTJAR_USER_ID = "2939969";

/**
 * height and with for chatbox
 */
export const ChatBoxDimensions = {
    DEFAULT_WIDTH: "496px",
    DEFAULT_HEIGHT: "560px",
    EXPANDED_WIDTH: "calc(100vw - (17% + 80px))",
    EXPANDED_HEIGHT: "calc(100vh - 74px)",
    EXPANDED_WIDTH_WITH_HIDDEN_MENU: "calc(100vw - 80px)",
};

/**
 * list of supported currencies on platform
 */
export const currencyList = [
    { label: "GBP", value: "GBP" },
    { label: "EUR", value: "EUR" },
    { label: "USD", value: "USD" },
];

export const buyerCurrencyList = [
    { label: "GBP", value: "GBP" },
    { label: "USD", value: "USD" },
];

export const monthFieldOptions = [
    { label: "1 month", value: 1 },
    { label: "2 months", value: 2 },
    { label: "3 months", value: 3 },
    { label: "4 months", value: 4 },
    { label: "5 months", value: 5 },
    { label: "6 months", value: 6 },
    { label: "7 months", value: 7 },
    { label: "8 months", value: 8 },
    { label: "9 months", value: 9 },
    { label: "10 months", value: 10 },
    { label: "11 months", value: 11 },
    { label: "12 months", value: 12 },
];

export const weekFieldOptions = [
    { label: "Every Week", value: 1 },
    { label: "Every 2 Weeks", value: 2 },
    { label: "Every 3 Weeks", value: 3 },
    { label: "Every 4 Weeks", value: 4 },
    { label: "Every 5 Weeks", value: 5 },
    { label: "Every 6 Weeks", value: 6 },
    { label: "Custom", value: 0 },
];

export const timePeriodOptions = [
    { label: "Days", value: "day" },
    { label: "Months", value: "month" },
    { label: "Years", value: "year" },
];

export const incoterms = [
    {
        label: "Carriage and Insurance Paid to (CIP)",
        value: "carriage_and_insurance_paid_to",
    },
    {
        label: "Carriage Paid To (CPT)",
        value: "carriage_paid_to",
    },
    {
        label: "Cost and Freight (CFR)",
        value: "cost_and_freight",
    },
    {
        label: "Cost, Insurance and Freight (CIF)",
        value: "cost_insurance_and_freight",
    },

    {
        label: "Delivered At Place (DAP)",
        value: "delivered_at_place",
    },
    { label: "Delivery Duty Paid (DDP)", value: "delivery_duty_paid" },
    { label: "Delivery Duty Unpaid (DDU)", value: "delivery_duty_unpaid" },
    {
        label: "Ex Works (EXW)",
        value: "ex_works",
    },
    {
        label: "Free Alongside Ship (FAS)",
        value: "free_alongside_ship",
    },
    {
        label: "Free on Board (FOB)",
        value: "free_on_board",
    },
];

export const feedbackItems = [
    {
        label: "Ingredient Requests",
        href: "https://opply.canny.io/ingredient-requests",
        testId: "feedback-menu__ingredient-request",
    },
    {
        label: "Feature Requests",
        href: "https://opply.canny.io/feature-requests",
        testId: "feedback-menu__feature-request",
    },
];

export const forcedLoginTimestamp = "2025-02-05T00:00:00Z";

export const appOrderPaymentTermsLabels: AppOrderPaymentTermsLabelsMap = {
    [AppOrderPaymentTermsEnum.PayBeforeDispatch]: "Pay before dispatch",
    [AppOrderPaymentTermsEnum.PayAfterDispatch]: "Pay after dispatch",
    [AppOrderPaymentTermsEnum.PayOn28thSecondMonthAfterDispatch]:
        "Pay on the 28th of the second month after dispatch",
    [AppOrderPaymentTermsEnum.PayEndOfMonthOfDispatchDate]:
        "Pay end of month of dispatch date",
};

export const appOrderPaymentTermsOptions = [
    {
        label: "Pay before dispatch",
        value: AppOrderPaymentTermsEnum.PayBeforeDispatch,
    },
    {
        label: "Pay after dispatch",
        value: AppOrderPaymentTermsEnum.PayAfterDispatch,
    },
    {
        label: "Pay end of month of dispatch date",
        value: AppOrderPaymentTermsEnum.PayEndOfMonthOfDispatchDate,
    },
];

export const appOrderPaymentProviderOptions = [
    {
        label: "Stripe",
        value: PaymentProviderEnum.Stripe,
    },
    {
        label: "Two",
        value: PaymentProviderEnum.Two,
    },
];

export const appOrderPaymentProviderWithDirectOptions = [
    {
        label: "Stripe",
        value: PaymentProviderEnum.Stripe,
    },
    {
        label: "Two",
        value: PaymentProviderEnum.Two,
    },
    {
        label: "Buyer Pays Supplier (OM Only)",
        value: PaymentProviderEnum.Direct,
    },
];

export const appOrderSupplierPaymentTermsLabels: AppOrderSupplierPaymentTermsLabelsMap =
    {
        [SupplierPayoutTermsEnum.PayoutBeforeDispatch]:
            "Payout before dispatch",
        [SupplierPayoutTermsEnum.PayoutAfterDispatch]: "Payout after dispatch",
    };

export const appOrderSupplierPayoutTermsOptions = [
    {
        label: "Payout before dispatch",
        value: SupplierPayoutTermsEnum.PayoutBeforeDispatch,
    },
    {
        label: "Payout after dispatch",
        value: SupplierPayoutTermsEnum.PayoutAfterDispatch,
    },
];

export const CREDIT_RISK_OWNER_NONE = "none" as const;

export const creditRiskOwnerOptions: Array<{
    label: string;
    value: CreditRiskOwnerEnum | typeof CREDIT_RISK_OWNER_NONE;
}> = [
    {
        label: "None",
        value: CREDIT_RISK_OWNER_NONE,
    },
    {
        label: "Two",
        value: CreditRiskOwnerEnum.Two,
    },
    {
        label: "Allianz",
        value: CreditRiskOwnerEnum.Allianz,
    },
    {
        label: "Opply",
        value: CreditRiskOwnerEnum.Opply,
    },
];

export const CREDIT_RISK_OWNER_DISPLAY: Record<CreditRiskOwnerEnum, string> = {
    [CreditRiskOwnerEnum.Two]: "Two",
    [CreditRiskOwnerEnum.Allianz]: "Allianz",
    [CreditRiskOwnerEnum.Opply]: "Opply",
};

export const supplierTypeLabels: Record<SupplierTypeEnum, string> = {
    [SupplierTypeEnum.Producer]: "Producer",
    [SupplierTypeEnum.Manufacturer]: "Manufacturer",
    [SupplierTypeEnum.Broker]: "Broker",
    [SupplierTypeEnum.Other]: "Other",
    [SupplierTypeEnum.NotApplicable]: "N/A",
};

export const buyerFinancialDocumentTypeLabels: Record<
    BuyerFinancialDocumentTypeEnum,
    string
> = {
    [BuyerFinancialDocumentTypeEnum.ManagementAccounts]: "Management Accounts",
    [BuyerFinancialDocumentTypeEnum.AuditedAccounts]: "Audited Accounts",
    [BuyerFinancialDocumentTypeEnum.BalanceSheet]: "Balance Sheet",
    [BuyerFinancialDocumentTypeEnum.ProfitAndLoss]: "Profit and Loss",
    [BuyerFinancialDocumentTypeEnum.CashflowForecast]: "Cashflow Forecast",
    [BuyerFinancialDocumentTypeEnum.SalesTaxExemption]: "Sales Tax Exemption",
    [BuyerFinancialDocumentTypeEnum.Other]: "Other",
};

export const supplierTypeOptions: Array<{
    label: string;
    value: SupplierTypeEnum | null;
}> = [
    { label: "Producer", value: SupplierTypeEnum.Producer },
    { label: "Manufacturer", value: SupplierTypeEnum.Manufacturer },
    { label: "Broker", value: SupplierTypeEnum.Broker },
    { label: "Other", value: SupplierTypeEnum.Other },
    { label: "N/A", value: SupplierTypeEnum.NotApplicable },
    { label: "Clear selection", value: null },
];

export const SHIPPING_PRODUCT_NAME = "Shipping";

export const opplyWiseBankAccountDetails = {
    "Account Holder": "Opply Ltd",
    "Account Number": "88640458",
    "Sort Code": "23-14-70",
};

export const opplyUSBankAccountDetails = {
    Name: "Opply Ltd",
    "Account number": "8311197001",
    "Routing number": "026073150",
    "Account type": "Checking",
};

export const twoBankAccountDetails = {
    Name: "Two B2B Ltd",
    "Account number": "05501024",
    "Sort code": "04-00-85",
};

export const twoUSBankAccountDetails = {
    Name: "Two B2B Ltd",
    "Account number": "51044425",
    "Routing number": "021000089",
};

export const buyerLifecycleStateLabels: LifecycleStateLabelsMap = {
    [LifecycleStateEnum.Canceled]: "Cancelled",
    [LifecycleStateEnum.Confirmed]: "Confirmed",
    [LifecycleStateEnum.Initializing]: "Initialising",
    [LifecycleStateEnum.PendingMerchantOfRecordApproval]: "Pending Approval",
    [LifecycleStateEnum.OrderReceived]: "Order Received",
    [LifecycleStateEnum.OrderSentToSupplier]: "Sent to Supplier",
    [LifecycleStateEnum.OrderConfirmingPrice]: "Confirming Price",
    [LifecycleStateEnum.Forecasted]: "Forecasted",
};

export const supplierLifecycleStateLabels: LifecycleStateLabelsMap = {
    [LifecycleStateEnum.Canceled]: "Cancelled",
    [LifecycleStateEnum.Confirmed]: "Confirmed",
    [LifecycleStateEnum.Initializing]: "Initialising",
    [LifecycleStateEnum.PendingMerchantOfRecordApproval]: "Pending Approval",
    [LifecycleStateEnum.OrderReceived]: "Order Received",
    [LifecycleStateEnum.OrderSentToSupplier]: "Sent to Supplier",
    [LifecycleStateEnum.OrderConfirmingPrice]: "Confirming Price",
    [LifecycleStateEnum.Forecasted]: "Forecasted",
};

export const buyerDeliveryStateLabels: DeliveryStateLabelsMap = {
    [DeliveryStateEnum.Initializing]: "Initialising",
    [DeliveryStateEnum.NotStarted]: "Not Started",
    [DeliveryStateEnum.PendingDispatch]: "Pending Dispatch",
    [DeliveryStateEnum.InTransit]: "In Transit",
    [DeliveryStateEnum.Delivered]: "Delivered",
    [LegacyDeliveryStatusEnum.Dispatched]: "In Transit",
    // To Be Confirmed path
    [DeliveryStateEnum.FreightOwnershipPending]: "Freight Ownership Pending",
    [DeliveryStateEnum.GatheringQuotes]: "Gathering Quotes",
    // Supplier path
    [DeliveryStateEnum.DispatchScheduled]: "Dispatch Scheduled",
    // Buyer path
    [DeliveryStateEnum.AwaitingCollectionDetails]:
        "Awaiting Collection Details",
    [DeliveryStateEnum.ReadyForPickup]: "Ready for Pickup",
    [DeliveryStateEnum.PickedUp]: "Picked Up",
    // MoR path
    [DeliveryStateEnum.PreparingFreight]: "Preparing Freight",
    [DeliveryStateEnum.FreightScheduled]: "Freight Scheduled",
    [DeliveryStateEnum.GatheringFreightQuotes]: "Gathering Freight Quotes",
    [DeliveryStateEnum.QuotesConfirmed]: "Quotes Confirmed",
};

export const supplierDeliveryStateLabels: DeliveryStateLabelsMap = {
    [DeliveryStateEnum.Initializing]: "Initialising",
    [DeliveryStateEnum.NotStarted]: "Not Started",
    [DeliveryStateEnum.PendingDispatch]: "Pending Dispatch",
    [DeliveryStateEnum.InTransit]: "In Transit",
    [DeliveryStateEnum.Delivered]: "Delivered",
    [LegacyDeliveryStatusEnum.Dispatched]: "In Transit",
    // To Be Confirmed path
    [DeliveryStateEnum.FreightOwnershipPending]: "Freight Ownership Pending",
    [DeliveryStateEnum.GatheringQuotes]: "Gathering Quotes",
    // Supplier path
    [DeliveryStateEnum.DispatchScheduled]: "Dispatch Scheduled",
    // Buyer path
    [DeliveryStateEnum.AwaitingCollectionDetails]:
        "Awaiting Collection Details",
    [DeliveryStateEnum.ReadyForPickup]: "Ready for Pickup",
    [DeliveryStateEnum.PickedUp]: "Picked Up",
    // MoR path
    [DeliveryStateEnum.PreparingFreight]: "Preparing Freight",
    [DeliveryStateEnum.FreightScheduled]: "Freight Scheduled",
    [DeliveryStateEnum.GatheringFreightQuotes]: "Gathering Freight Quotes",
    [DeliveryStateEnum.QuotesConfirmed]: "Quotes Confirmed",
};

export const payInStateLabels: PayInStateLabelsMap = {
    [PayInStateEnum.Initializing]: "Initialising",
    [PayInStateEnum.NotStarted]: "Not Started",
    [PayInStateEnum.Due]: "Due",
    [PayInStateEnum.Overdue]: "Overdue",
    [PayInStateEnum.AwaitingReconciliation]: "Awaiting Reconciliation",
    [PayInStateEnum.Paid]: "Paid",
    [PayInStateEnum.Refunded]: "Refunded",
    [PayInStateEnum.PartiallyRefunded]: "Partially Refunded",
    [LegacyPaymentStatusEnum.PaymentUpcoming]: "Due",
    [LegacyPaymentStatusEnum.PaymentProcessing]: "Due",
    [LegacyPaymentStatusEnum.PaymentDue]: "Due",
};

export const payoutStateLabels: PayoutStateLabelsMap = {
    [PayoutStateEnum.Initializing]: "Initialising",
    [PayoutStateEnum.NotStarted]: "Not Started",
    [PayoutStateEnum.Due]: "Payment Upcoming",
    [PayoutStateEnum.Overdue]: "Payment Upcoming",
    [PayoutStateEnum.Paid]: "Paid",
    [PayoutStateEnum.Refunded]: "Refunded",
    [PayoutStateEnum.PartiallyRefunded]: "Partially Refunded",
};
export const unitOfMeasurements = [
    "fl oz",
    "g",
    "mg",
    "gals",
    "kg",
    "L",
    "tons",
    "oz",
    "lbs",
    "pcs",
    "pt",
    "ml",
    "boxes",
    "pallets",
    "tsp",
    "tbsp",
    "units",
    "cases",
    "trays",
];

export const unitOfMeasurementOptions = unitOfMeasurements.map((unit) => ({
    label: unit,
    value: unit,
}));

export const STRIPE_INVOICE_BASE_URL = "https://dashboard.stripe.com/invoices";

export const ZENDESK_TICKET_BASE_URL =
    "https://opply.zendesk.com/agent/tickets";

export const orderLineItemDocumentCategories = [
    { value: CatalogItemDocumentCategoryEnum.SpecSheet, label: "Spec" },
    {
        value: CatalogItemDocumentCategoryEnum.CertificateOfAnalysis,
        label: "COA",
    },
    {
        value: CatalogItemDocumentCategoryEnum.BritishRetailConsortium,
        label: "BRC",
    },
    {
        value: CatalogItemDocumentCategoryEnum.InternationalOrganisationForStandardisation,
        label: "ISO",
    },
    {
        value: CatalogItemDocumentCategoryEnum.FoundationFoodSafetySystemCertification,
        label: "FSSC",
    },
    {
        value: CatalogItemDocumentCategoryEnum.InternationalFeaturedStandards,
        label: "IFS",
    },
    {
        value: CatalogItemDocumentCategoryEnum.SafeQualityFood,
        label: "SQF",
    },
];

export const orderDocumentTypes = [
    { value: AppOrderDocumentType.DeliveryNote, label: "Delivery Note" },
    { value: AppOrderDocumentType.Halal, label: "Halal Certificate" },
    { value: AppOrderDocumentType.Organic, label: "Organic Certificate" },
    { value: AppOrderDocumentType.Kosher, label: "Kosher Certificate" },
    { value: AppOrderDocumentType.TradingSchedule, label: "Trading Schedule" },
    {
        value: AppOrderDocumentType.CertificateOfConformity,
        label: "Certificate of Conformity",
    },
    { value: AppOrderDocumentType.PackingList, label: "Packing List" },
    {
        value: AppOrderDocumentType.TechnicalDataSheet,
        label: "Technical Data Sheet",
    },
    {
        value: AppOrderDocumentType.CertificateOfInsurance,
        label: "Certificate of Insurance",
    },
    {
        value: AppOrderDocumentType.CustomsDeclarationSummary,
        label: "Customs Declaration Summary",
    },
    {
        value: AppOrderDocumentType.BillOfLading,
        label: "Bill of Lading",
    },
    {
        value: AppOrderDocumentType.ProofOfPayment,
        label: "Proof of Payment",
    },
];

export const serviceTypeOptions = [
    {
        label: "Ingredient Sourcing",
        value: ServiceTypeEnum.IngredientSourcing,
    },
    {
        label: "Sampling",
        value: ServiceTypeEnum.Sampling,
    },
    {
        label: "Other",
        value: ServiceTypeEnum.Other,
    },
];

export const spotBuyDocumentCategoryLabel: Record<
    SpotBuyOrderDocumentCategory,
    string
> = {
    po: "PO",
    ingredientSpec: "Ingredient Spec",
};

export const spotBuyOrderDocumentCategory: Record<
    string,
    SpotBuyOrderDocumentCategory
> = {
    PO: "po",
    INGREDIENT_SPEC: "ingredientSpec",
};

export const orderActivityCategories = [
    {
        value: OrderActivityCategory.ConfirmingPrice,
        label: "Confirming Price",
    },
    { value: OrderActivityCategory.PriceConfirmed, label: "Price Confirmed" },
    {
        value: OrderActivityCategory.ConfirmingDeliveryDate,
        label: "Confirming Delivery",
    },
    {
        value: OrderActivityCategory.DeliveryDateConfirmed,
        label: "Delivery Confirmed",
    },
    { value: OrderActivityCategory.Note, label: "Note" },
    {
        value: OrderActivityCategory.OrderPutOnHold,
        label: "Order Put On Hold",
    },
    { value: OrderActivityCategory.OrderResumed, label: "Order Resumed" },
    { value: OrderActivityCategory.OrderReceived, label: "Order Received" },
    {
        value: OrderActivityCategory.OrderSentToSupplier,
        label: "Order Sent To Supplier",
    },
    {
        value: OrderActivityCategory.OrderConfirmingPrice,
        label: "Order Confirming Price",
    },
    {
        value: OrderActivityCategory.OrderForecasted,
        label: "Order Forecasted",
    },
    {
        value: OrderActivityCategory.OrderUnforecasted,
        label: "Order Unforecasted",
    },
    {
        value: OrderActivityCategory.RefundProcessed,
        label: "Refund Processed",
    },
    {
        value: OrderActivityCategory.PaymentTermsChanged,
        label: "Payment Terms Changed",
    },
    {
        value: OrderActivityCategory.OrderPushedToTwo,
        label: "Order Pushed To Two",
    },
];

export const MAX_DECIMALS_UNIT_PRICE = 6;
export const MAX_DECIMALS_QUANTITY = 3;

export const activityTypeMessageMap = {
    confirming_price:
        "We are confirming the price with the supplier. We will update you once we have a confirmed price.",
    price_confirmed:
        "The prices in your order have been confirmed with the supplier.",
    confirming_delivery_date:
        "We are confirming the delivery date with the supplier. We will update you once we have a confirmed delivery date.",
    delivery_date_confirmed:
        "The delivery date has been confirmed with the supplier.",
    order_put_on_hold: "Order put on hold.",
    order_resumed: "Order resumed from hold.",
};

export const paymentProviderLabels: PaymentProviderLabelsMap = {
    [PaymentProviderEnum.Stripe]: "Stripe",
    [PaymentProviderEnum.Two]: "Two",
    [PaymentProviderEnum.Direct]: "Direct",
};

export const paymentStatusOptions = [
    { label: "Due", value: PayInStateEnum.Due },
    { label: "Paid", value: PayInStateEnum.Paid },
    { label: "Overdue", value: PayInStateEnum.Overdue },
];

export const buyerOnboardingCountryLabels: Record<
    BuyerOnboardingCountry,
    string
> = {
    [BuyerOnboardingCountry.Us]: "United States",
    [BuyerOnboardingCountry.Uk]: "United Kingdom",
};

export const shippingResponsibilityLabels: Record<
    ShippingResponsibilityEnum,
    string
> = {
    [ShippingResponsibilityEnum.Buyer]: "Buyer",
    [ShippingResponsibilityEnum.Supplier]: "Supplier",
    [ShippingResponsibilityEnum.ToBeConfirmed]: "To Be Confirmed",
    [ShippingResponsibilityEnum.MerchantOfRecord]: "Merchant of Record",
    [ShippingResponsibilityEnum.Unknown]: "Unknown",
};

export const shippingResponsibilityOptions = [
    {
        label: shippingResponsibilityLabels[ShippingResponsibilityEnum.Buyer],
        value: ShippingResponsibilityEnum.Buyer,
    },
    {
        label: shippingResponsibilityLabels[
            ShippingResponsibilityEnum.Supplier
        ],
        value: ShippingResponsibilityEnum.Supplier,
    },
    {
        label: shippingResponsibilityLabels[
            ShippingResponsibilityEnum.MerchantOfRecord
        ],
        value: ShippingResponsibilityEnum.MerchantOfRecord,
    },
    {
        label: shippingResponsibilityLabels[
            ShippingResponsibilityEnum.ToBeConfirmed
        ],
        value: ShippingResponsibilityEnum.ToBeConfirmed,
    },
];

/** Shipping responsibility options that can be confirmed (excludes ToBeConfirmed and Unknown) */
export const confirmableShippingResponsibilityOptions = [
    {
        label: shippingResponsibilityLabels[
            ShippingResponsibilityEnum.Supplier
        ],
        value: ConfirmShippingResponsibilityShippingResponsibilityEnum.Supplier,
    },
    {
        label: shippingResponsibilityLabels[ShippingResponsibilityEnum.Buyer],
        value: ConfirmShippingResponsibilityShippingResponsibilityEnum.Buyer,
    },
    {
        label: shippingResponsibilityLabels[
            ShippingResponsibilityEnum.MerchantOfRecord
        ],
        value: ConfirmShippingResponsibilityShippingResponsibilityEnum.MerchantOfRecord,
    },
];

export const reportingUnitLabelSymbol: Record<string, string> = {
    // Weight
    [ReportingUnitsEnum.G]: "g",
    [ReportingUnitsEnum.Kg]: "kg",
    [ReportingUnitsEnum.Mg]: "mg",
    [ReportingUnitsEnum.LongTons]: "tons (Long)",
    [ReportingUnitsEnum.ShortTons]: "tons (Short)",
    [ReportingUnitsEnum.MetricTons]: "tons (Metric)",
    [ReportingUnitsEnum.Oz]: "oz",
    [ReportingUnitsEnum.Lbs]: "lbs",

    // Volume
    [ReportingUnitsEnum.UkFlOz]: "fl. oz. (UK)",
    [ReportingUnitsEnum.UsFlOz]: "fl oz. (US)",
    [ReportingUnitsEnum.UkGals]: "gals (UK)",
    [ReportingUnitsEnum.UsGals]: "gals (US)",
    [ReportingUnitsEnum.L]: "l",
    [ReportingUnitsEnum.Ml]: "ml",
    [ReportingUnitsEnum.UkPt]: "pt (UK)",
    [ReportingUnitsEnum.UsPt]: "pt (US)",

    // yes, teaspoons and tablespoons are capitalised differently. it's driving me crazy too
    [ReportingUnitsEnum.UkTsp]: "tsp (UK)",
    [ReportingUnitsEnum.UkTbsp]: "Tbsp (UK)",
    [ReportingUnitsEnum.UsTsp]: "tsp (US)",
    [ReportingUnitsEnum.UsTbsp]: "Tbsp (US)",
    [ReportingUnitsEnum.MetricTsp]: "tsp (Metric)",
    [ReportingUnitsEnum.MetricTbsp]: "Tbsp (Metric)",

    // Count
    [ReportingUnitsEnum.Pcs]: "pcs",
    [ReportingUnitsEnum.X1000Pcs]: "x 1000 pcs",
};

export const ReportingUnitsByWeight = [
    { label: "Grams", value: ReportingUnitsEnum.G },
    { label: "Kilograms", value: ReportingUnitsEnum.Kg },
    { label: "Milligrams", value: ReportingUnitsEnum.Mg },
    { label: "Ounces", value: ReportingUnitsEnum.Oz },
    { label: "Pounds", value: ReportingUnitsEnum.Lbs },
    { label: "Tons (Long)", value: ReportingUnitsEnum.LongTons },
    { label: "Tons (Metric)", value: ReportingUnitsEnum.MetricTons },
    { label: "Tons (Short)", value: ReportingUnitsEnum.ShortTons },
];

export const ReportingUnitsByVolume = [
    { label: "Fluid Ounces (UK)", value: ReportingUnitsEnum.UkFlOz },
    { label: "Fluid Ounces (US)", value: ReportingUnitsEnum.UsFlOz },
    { label: "Gallons (UK)", value: ReportingUnitsEnum.UkGals },
    { label: "Gallons (US)", value: ReportingUnitsEnum.UsGals },
    { label: "Litres", value: ReportingUnitsEnum.L },
    { label: "Millilitres", value: ReportingUnitsEnum.Ml },
    { label: "Pints (UK)", value: ReportingUnitsEnum.UkPt },
    { label: "Pints (US)", value: ReportingUnitsEnum.UsPt },
    { label: "Teaspoons (Metric)", value: ReportingUnitsEnum.MetricTsp },
    { label: "Teaspoons (UK)", value: ReportingUnitsEnum.UkTsp },
    { label: "Teaspoons (US)", value: ReportingUnitsEnum.UsTsp },
    { label: "Tablespoons (Metric)", value: ReportingUnitsEnum.MetricTbsp },
    { label: "Tablespoons (UK)", value: ReportingUnitsEnum.UkTbsp },
    { label: "Tablespoons (US)", value: ReportingUnitsEnum.UsTbsp },
];

export const ReportingUnitsByCount = [
    { label: "Pieces", value: ReportingUnitsEnum.Pcs },
    { label: "1,000 Pieces", value: ReportingUnitsEnum.X1000Pcs },
];

export const GB_SERVICE_FEE_TAX_RATE = 20;
export const USD_SERVICE_FEE_TAX_RATE = 0;
export const DEFAULT_SERVICE_FEE_TAX_RATE = 0;

/**
 * US States list for address forms
 */
export const US_STATES = [
    { label: "Alabama", value: "AL" },
    { label: "Alaska", value: "AK" },
    { label: "Arizona", value: "AZ" },
    { label: "Arkansas", value: "AR" },
    { label: "California", value: "CA" },
    { label: "Colorado", value: "CO" },
    { label: "Connecticut", value: "CT" },
    { label: "Delaware", value: "DE" },
    { label: "Florida", value: "FL" },
    { label: "Georgia", value: "GA" },
    { label: "Hawaii", value: "HI" },
    { label: "Idaho", value: "ID" },
    { label: "Illinois", value: "IL" },
    { label: "Indiana", value: "IN" },
    { label: "Iowa", value: "IA" },
    { label: "Kansas", value: "KS" },
    { label: "Kentucky", value: "KY" },
    { label: "Louisiana", value: "LA" },
    { label: "Maine", value: "ME" },
    { label: "Maryland", value: "MD" },
    { label: "Massachusetts", value: "MA" },
    { label: "Michigan", value: "MI" },
    { label: "Minnesota", value: "MN" },
    { label: "Mississippi", value: "MS" },
    { label: "Missouri", value: "MO" },
    { label: "Montana", value: "MT" },
    { label: "Nebraska", value: "NE" },
    { label: "Nevada", value: "NV" },
    { label: "New Hampshire", value: "NH" },
    { label: "New Jersey", value: "NJ" },
    { label: "New Mexico", value: "NM" },
    { label: "New York", value: "NY" },
    { label: "North Carolina", value: "NC" },
    { label: "North Dakota", value: "ND" },
    { label: "Ohio", value: "OH" },
    { label: "Oklahoma", value: "OK" },
    { label: "Oregon", value: "OR" },
    { label: "Pennsylvania", value: "PA" },
    { label: "Rhode Island", value: "RI" },
    { label: "South Carolina", value: "SC" },
    { label: "South Dakota", value: "SD" },
    { label: "Tennessee", value: "TN" },
    { label: "Texas", value: "TX" },
    { label: "Utah", value: "UT" },
    { label: "Vermont", value: "VT" },
    { label: "Virginia", value: "VA" },
    { label: "Washington", value: "WA" },
    { label: "West Virginia", value: "WV" },
    { label: "Wisconsin", value: "WI" },
    { label: "Wyoming", value: "WY" },
];

/**
 * Delivery location type options for buyer onboarding
 */
export const DELIVERY_LOCATION_TYPE_OPTIONS = [
    { label: "Warehouse", value: "warehouse" },
    { label: "Factory", value: "factory" },
    { label: "Distribution Center", value: "distribution_center" },
    { label: "Retail Location", value: "retail_location" },
    { label: "Other", value: "other" },
];

/**
 * Service fee type options for order management contracts
 */
export const serviceFeeTypeOptions = [
    {
        label: "Order Management Only",
        value: ServiceFeeTypeEnum.OrderManagementOnly,
    },
    {
        label: "Order Management with Payment Support",
        value: ServiceFeeTypeEnum.OrderManagementWithPaymentSupport,
    },
];

export const serviceFeeTypeShortLabels: Record<ServiceFeeTypeEnum, string> = {
    [ServiceFeeTypeEnum.OrderManagementOnly]: "OM Only",
    [ServiceFeeTypeEnum.OrderManagementWithPaymentSupport]:
        "OM with Payment Support",
};

/**
 * Labels for order hold internal reasons
 */
export const ORDER_HOLD_INTERNAL_REASON_LABELS: Record<
    InternalReasonEnum,
    string
> = {
    [InternalReasonEnum.SupplierDelay]: "Supplier Delay",
    [InternalReasonEnum.VolumeConfirmation]: "Volume Confirmation",
    [InternalReasonEnum.CreditReview]: "Credit Review",
    [InternalReasonEnum.DocumentsRequired]: "Documents Required",
    [InternalReasonEnum.FraudReview]: "Fraud Review",
    [InternalReasonEnum.Forecasting]: "Forecasting",
    [InternalReasonEnum.OverduePayments]: "Overdue Payments",
    [InternalReasonEnum.Other]: "Other",
};

/**
 * Quality issue type options for OTIF data
 */
export const qualityIssueTypeOptions = [
    { label: "Spoilage", value: QualityIssueTypeEnum.Spoilage },
    { label: "Doesn't Match Spec", value: QualityIssueTypeEnum.NotMatchSpec },
    { label: "Contamination", value: QualityIssueTypeEnum.Contamination },
    { label: "Discolouration", value: QualityIssueTypeEnum.Discolouration },
    { label: "Shelf Life Issue", value: QualityIssueTypeEnum.ShelfLifeIssue },
];

/**
 * Labels for service fee types
 */
export const serviceFeeTypeLabels: Record<ServiceFeeTypeEnum, string> = {
    [ServiceFeeTypeEnum.OrderManagementOnly]: "Order Management Only",
    [ServiceFeeTypeEnum.OrderManagementWithPaymentSupport]:
        "Order Management with Payment Support",
};

/**
 * Shared color theme for lifecycle intermediate order statuses
 * (OrderReceived, OrderSentToSupplier, OrderConfirmingPrice)
 */
export const INTERMEDIATE_COLOR_THEME = {
    textColor: "tw-text-purple-palette-2",
    backgroundColor: "tw-bg-purple-palette-2/15",
    tooltipColor: "purple-palette-2",
};

/**
 * Raw background color for forecasted order status
 */
export const FORECASTED_BG_COLOR = "#E6F0FF";

/**
 * Shared color theme for forecasted order status
 */
export const FORECASTED_COLOR_THEME = {
    textColor: "tw-text-[#1E4FD8]",
    backgroundColor: `tw-bg-[${FORECASTED_BG_COLOR}]`,
    tooltipColor: "[#1E4FD8]",
    iconColor: "tw-text-[#1E4FD8]",
};

/**
 * Lifecycle intermediate status options for order status transitions
 */
export const LIFECYCLE_INTERMEDIATE_STATUS_OPTIONS: Array<{
    label: string;
    value: LifecycleStateEnum;
}> = [
    { label: "Order Received", value: LifecycleStateEnum.OrderReceived },
    {
        label: "Sent to Supplier",
        value: LifecycleStateEnum.OrderSentToSupplier,
    },
    {
        label: "Confirming Price",
        value: LifecycleStateEnum.OrderConfirmingPrice,
    },
];

/**
 * Lifecycle intermediate state values derived from LIFECYCLE_INTERMEDIATE_STATUS_OPTIONS.
 * Use this for state membership checks instead of duplicating the array.
 */
export const LIFECYCLE_INTERMEDIATE_STATES: LifecycleStateEnum[] =
    LIFECYCLE_INTERMEDIATE_STATUS_OPTIONS.map((option) => option.value);

export type CreditTypeKey = "two_organic" | "two_recourse" | "allianz";

// Note the key-to-label mapping is not 1:1 with the words in the labels: both
// `two_recourse` and `allianz` display as "Allianz (...)". `two_recourse` is the
// Two-recourse column, and `allianz` is the Stripe-backed Allianz column. So
// excluding the `allianz` key (e.g. useCreditChecker `excludedLimitKeys`) hides
// "Allianz (Stripe)" only, NOT the recourse column.
export const CREDIT_TYPE_LABELS: Record<CreditTypeKey, string> = {
    two_organic: "Two Organic",
    two_recourse: "Allianz (Recourse)",
    allianz: "Allianz (Stripe)",
};

export function getCreditTypeKey(
    paymentProvider: PaymentProviderEnum | null | undefined,
    creditRiskOwner: CreditRiskOwnerEnum | null | undefined
): CreditTypeKey | null {
    if (!paymentProvider || !creditRiskOwner) return null;
    if (
        paymentProvider === PaymentProviderEnum.Two &&
        creditRiskOwner === CreditRiskOwnerEnum.Two
    )
        return "two_organic";
    if (
        paymentProvider === PaymentProviderEnum.Two &&
        creditRiskOwner === CreditRiskOwnerEnum.Allianz
    )
        return "two_recourse";
    if (
        paymentProvider === PaymentProviderEnum.Stripe &&
        creditRiskOwner === CreditRiskOwnerEnum.Allianz
    )
        return "allianz";
    return null;
}

export function getCreditTypeLabel(
    paymentProvider: PaymentProviderEnum | null | undefined,
    creditRiskOwner:
        | CreditRiskOwnerEnum
        | typeof CREDIT_RISK_OWNER_NONE
        | null
        | undefined
): string | null {
    if (creditRiskOwner === CREDIT_RISK_OWNER_NONE) return null;
    const key = getCreditTypeKey(paymentProvider, creditRiskOwner);
    return key ? CREDIT_TYPE_LABELS[key] : null;
}

export const TRADING_NAME_LABEL = "Trading Name";
export const REGISTERED_NAME_LABEL = "Registered Name";
export const HQ_CITY_LABEL = "HQ City";
export const TRADING_NAME_TOOLTIP =
    "The name used for the company in marketing materials, branding, etc. (the branded name).";
export const REGISTERED_NAME_TOOLTIP =
    "The name under which the company is officially registered (e.g. in the UK's Companies House registry).";
// Must stay in sync with the buyer onboarding copy in the buyer app.
export const HQ_CITY_TOOLTIP =
    "The city your team primarily works from. May differ from your registered address.";
